import "./fontSize.scss";
import React, { useCallback, useEffect, useState } from "react";
import { $patchStyleText } from "@lexical/selection";
import { $getSelection, LexicalEditor } from "lexical";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CustomTooltip from "../../custom-tooltip";

const MIN_ALLOWED_FONT_SIZE = 8;
const MAX_ALLOWED_FONT_SIZE = 72;
const DEFAULT_FONT_SIZE = 15;

type UpdateFontSizeType = "increment" | "decrement";

const updateFontSizeType = {
  increment: "increment" as UpdateFontSizeType,
  decrement: "decrement" as UpdateFontSizeType,
};

interface FontSizeProps {
  selectionFontSize: string;
  disabled: boolean;
  editor: LexicalEditor;
}

export default function FontSize({
  selectionFontSize,
  disabled,
  editor,
}: FontSizeProps): any {
  const [inputValue, setInputValue] = useState<string>(selectionFontSize);

  const calculateNextFontSize = (
    currentFontSize: number,
    updateType: UpdateFontSizeType
  ): number => {
    let updatedFontSize = currentFontSize;

    if (updateType === updateFontSizeType.decrement) {
      switch (true) {
        case currentFontSize > MAX_ALLOWED_FONT_SIZE:
          updatedFontSize = MAX_ALLOWED_FONT_SIZE;
          break;
        case currentFontSize >= 48:
          updatedFontSize -= 12;
          break;
        case currentFontSize >= 24:
          updatedFontSize -= 4;
          break;
        // case currentFontSize >= 14:
        //   updatedFontSize -= 2;
        //   break;
        case currentFontSize >= 9:
          updatedFontSize -= 1;
          break;
        default:
          updatedFontSize = MIN_ALLOWED_FONT_SIZE;
      }
    } else if (updateType === updateFontSizeType.increment) {
      switch (true) {
        case currentFontSize < MIN_ALLOWED_FONT_SIZE:
          updatedFontSize = MIN_ALLOWED_FONT_SIZE;
          break;
        // case currentFontSize < 12:
        //   updatedFontSize += 1;
        //   break;
        case currentFontSize < 20:
          updatedFontSize += 1;
          break;
        case currentFontSize < 36:
          updatedFontSize += 4;
          break;
        case currentFontSize <= 60:
          updatedFontSize += 12;
          break;
        default:
          updatedFontSize = MAX_ALLOWED_FONT_SIZE;
      }
    }

    return updatedFontSize;
  };

  const updateFontSizeInSelection = useCallback(
    (newFontSize: string) => {
      editor.update(() => {
        if (editor.isEditable()) {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, {
              "font-size": newFontSize,
            });
          }
        }
      });
    },
    [editor]
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const inputValueNumber = Number(inputValue);

    if (["e", "E", "+", "-"].includes(e.key) || isNaN(inputValueNumber)) {
      e.preventDefault();
      setInputValue("");
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      let updatedFontSize = inputValueNumber;
      if (inputValueNumber > MAX_ALLOWED_FONT_SIZE) {
        updatedFontSize = MAX_ALLOWED_FONT_SIZE;
      } else if (inputValueNumber < MIN_ALLOWED_FONT_SIZE) {
        updatedFontSize = MIN_ALLOWED_FONT_SIZE;
      }

      setInputValue(String(updatedFontSize));
      updateFontSizeInSelection(`${updatedFontSize}px`);
    }
  };

  const handleButtonClick = (updateType: UpdateFontSizeType): void => {
    const parsed = Number(inputValue);
    if (!isNaN(parsed)) {
      const nextFontSize = calculateNextFontSize(parsed, updateType);
      updateFontSizeInSelection(`${nextFontSize}px`);
      setInputValue(String(nextFontSize));
    } else {
      const fallbackSize =
        updateType === updateFontSizeType.increment
          ? calculateNextFontSize(DEFAULT_FONT_SIZE, updateType)
          : calculateNextFontSize(
              DEFAULT_FONT_SIZE,
              updateFontSizeType.decrement
            );
      updateFontSizeInSelection(`${fallbackSize}px`);
      setInputValue(String(fallbackSize));
    }
  };

  useEffect(() => {
    setInputValue(selectionFontSize);
  }, [selectionFontSize]);

  return (
    <>
      <button
        type="button"
        disabled={
          disabled ||
          (selectionFontSize !== "" &&
            Number(inputValue) <= MIN_ALLOWED_FONT_SIZE)
        }
        onClick={() => handleButtonClick(updateFontSizeType.decrement)}
        className="toolbar-item font-decrement add-icon"
      >
        <CustomTooltip title="Decrease font size" placement="bottom" arrow={false}>
          <RemoveIcon fontSize="small" />
        </CustomTooltip>
      </button>

      <input
        type="number"
        value={inputValue}
        disabled={disabled}
        className="toolbar-item font-size-input"
        min={MIN_ALLOWED_FONT_SIZE}
        max={MAX_ALLOWED_FONT_SIZE}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyPress}
      />

      <button
        type="button"
        disabled={
          disabled ||
          (selectionFontSize !== "" &&
            Number(inputValue) >= MAX_ALLOWED_FONT_SIZE)
        }
        onClick={() => handleButtonClick(updateFontSizeType.increment)}
        className="toolbar-item font-increment minus-icon"
      >
        <CustomTooltip title="Increase font size" placement="bottom" arrow={false}>
          <AddIcon fontSize="small" />
        </CustomTooltip>
      </button>
    </>
  );
}
